import { Component, h, Prop, State } from '@stencil/core';
import spacetime from 'spacetime';

import { getIncrementClasses, incrementClicked, isIncrementPast } from '../../utils/calendar';
import { settings } from '../../store/settings.store';
import { schedule } from '../../store/schedule.store';

import { ScheduleAvailableIncrement, ScheduleCalendarSpace } from '@artask/tambr-models/models/schedule.model';
import { Space } from '@artask/tambr-models/models/spaces.model';
import { Category } from '@artask/tambr-models/models/category.model';
import { Location } from '@artask/tambr-models/models/location.model';



@Component({
  tag: 'tambr-calendar-grid-view',
  styleUrl: 'tambr-calendar-grid-view.css',
  shadow: true,
})
export class TambrCalendarGridView {

  @Prop() selectedSpaces: string[];
  @Prop() categories: Category[];
  @Prop() locations: Location[];

  @State() showLeftScroll: boolean = false;
  @State() showRightScroll: boolean = false;

  @State() spaceInfo: Space;

  calendarRef: HTMLElement;
  calendarHeaderRef: HTMLElement;

  get increments() {
    const hidePastSlots = settings.businessSettings?.widgetLayoutSettings?.hidePastSlots;
    const increments = schedule.dateSchedule.increments;
    if ( hidePastSlots?.includes('grid') ) {
      return increments?.filter( slot => !isIncrementPast( schedule.dateSchedule.spaces[0].id, slot.timestamp ) );
    }
    return increments;
  }

  componentDidLoad() {
    this.updateButtonVisibility();
    if ( this.calendarRef ) this.calendarRef.addEventListener( 'scroll', () => this.updateButtonVisibility() );
    window.addEventListener('resize', () => this.updateButtonVisibility() );
  }

  componentDidRender() {
    this.updateButtonVisibility();
  }

  disconnectedCallback() {
    if ( this.calendarRef ) this.calendarRef.removeEventListener( 'scroll', () => this.updateButtonVisibility() );
    window.removeEventListener('resize', () => this.updateButtonVisibility() );
  }

  updateButtonVisibility() {
    var el = this.calendarRef;
    if ( !el ) return;
    this.showRightScroll = el.scrollLeft <= ( el.scrollWidth - el.offsetWidth - 20 );
    this.showLeftScroll = el.scrollLeft > 20;
  }


  getIncrementSize() {
    switch( settings?.businessSettings?.scheduleSettings?.scheduleIncrement ) {
      case 60: return 'large-slots';
      case 30: return 'medium-slots';
      case 15: return 'small-slots';
      default: return 'small-slots';
    };      
  }

  scrollRightHandle() {
    const left = this.calendarRef.scrollLeft + this.calendarRef.offsetWidth * 0.5;
    this.calendarRef.scrollTo({ top: 0, left, behavior: 'smooth' });
  }

  scrollLeftHandle() {
    const left = this.calendarRef.scrollLeft - this.calendarRef.offsetWidth * 0.5;
    this.calendarRef.scrollTo({ top: 0, left, behavior: 'smooth' });
  }


  openSpaceInfoModal( space: Space ) {
    if ( !!settings.demo ) return;
    const spaceInfoEl: HTMLTambrSpaceInfoDialogElement = document.createElement('tambr-space-info-dialog');
    spaceInfoEl.spaceInfo = space;
    spaceInfoEl.addEventListener( 'tambrSpaceInfoClose', () => spaceInfoEl.remove() );
    document.body.appendChild( spaceInfoEl );
  }


  render() {
    return ([
      <div class={ 'calendar ' + this.getIncrementSize() }>
      
        { this.renderIncrementLineGrid() }

        <div class='calendar-grid'>
          <div class='calendar-spacer'></div>
          
          { this.leftArrow() }

          <div class='calendar-content' ref={ (ref) => this.calendarRef = ref }>            
            { this.renderSpaceHeaders() }    
            <div class='calendar-columns'>
              { this.renderSpaces() }
            </div>          
          </div>

          { this.rightArrow() }  
        </div>

      </div>
      
    ]);
  }


  renderIncrementLineGrid() {
    if ( !this.increments?.length ) return;
    
    // add an extra label and line at the bottom
    const increments = [ ...this.increments ];
    const lastIncrement = increments[ increments.length - 1 ];
    const lastHour = spacetime( lastIncrement.timestamp, settings.businessSettings.timezone ).add( schedule.dateSchedule.scheduleIncrement, 'minutes' );
    increments.push({ appearance: 'primary', timestamp: lastHour.toNativeDate().getTime(), label: lastHour.format('time') });    
    
    return (
      <div class='calendar-grid-slots'>
        <div class='hidden-headers'>{ this.renderSpaceHeaders() }</div>

        { increments.map( ( increment: ScheduleAvailableIncrement ) => (
          <div class='calendar-grid-slot'>
            <div class={ 'calendar-grid-slot-label ' + increment.appearance }>
              { ( increment.appearance == 'hidden' ) ? '' : increment.label }
            </div>
            <div class='calendar-grid-slot-line'></div>
          </div>
        ) ) }        
      </div>
    );
  }


  renderSpaceHeaders() {
    return (
      <div class='calendar-headers' ref={ ref => this.calendarHeaderRef = ref }>
        { schedule.dateSchedule.spaces?.map( ( space: Space ) => (
          <div class='calendar-header' 
            hidden={ !this.selectedSpaces.includes( space.id ) } 
            onClick={ () => this.openSpaceInfoModal( space ) }>
              { this.renderSpaceHeader( schedule.dateSchedule.calendar[ space.id ] ) }
          </div>
        ))}
      </div>
    );
  }


  renderSpaceHeader( space: ScheduleCalendarSpace ) {
    return ([
      ( space.locationTitle ? <div class='calendar-header-subtitle'>{ space.locationTitle }</div> : '' ),
      <div class='calendar-header-title'>{ space.title }</div>,
      ( space.categoryTitle ? <div class='calendar-header-subtitle'>{  space.categoryTitle }</div> : '' )
    ]);
  }


  renderSpaces(){
    if ( !schedule.dateSchedule.spaces.length ) return <div>No available spaces</div>
    return schedule.dateSchedule.spaces?.map( (space: Space) => this.renderSpace(space) );
  }


  renderSpace( space: Space ){
    return (
      <div class='calendar-column' hidden={ !this.selectedSpaces.includes( space.id ) }>
        { this.increments.map( ( increment: ScheduleAvailableIncrement ) => (
          <div class={ getIncrementClasses( space.id, increment.timestamp ) } 
            onClick={ () => incrementClicked( space.id, increment.timestamp ) }>
              <tambr-icon type='plus' class='plus' />
              <div class='hover-text'>{ spacetime( increment.timestamp, settings.businessSettings.timezone ).format('time') }</div>
          </div>
        ) ) }
      </div>
    )
  }


  leftArrow() {
    const elClass = 'arrow-button-background' + ( this.showLeftScroll ? ' show' : '' );
    return (
      <div id='scroll-left' 
        class={ elClass } 
        style={{ 'height': `${ this.calendarHeaderRef?.clientHeight ? this.calendarHeaderRef.clientHeight + 'px' : 'none' }` }} 
        onClick={ () => this.scrollLeftHandle() }>
          <div class='tambr-arrow-button'>
            <tambr-icon type='left-arrow' class='arrow'></tambr-icon>
          </div>
      </div>
    )
  }

  rightArrow() {
    const elClass = 'arrow-button-background' + ( this.showRightScroll ? ' show' : '' );
    return (
      <div id='scroll-right' 
        class={ elClass } 
        style={{ 'height': `${ this.calendarHeaderRef?.clientHeight ? this.calendarHeaderRef.clientHeight + 'px' : 'none' }` }} 
        onClick={ () => this.scrollRightHandle() }>
          <div class='tambr-arrow-button'>
            <tambr-icon type='right-arrow' class='arrow'></tambr-icon>
          </div>
      </div>
    )
  }

}